import React, { useEffect, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';

import styles from './DroppableArea.module.scss';
import DraggableItem from './DraggableItem';
import { Item } from './Matching';

type DroppableAreaProps = Item & {
  correct?: boolean;
  incorrect?: boolean;
  disabled?: boolean;
  minHeight?: number;
  width?: number;
  onMount?: (data: MountData) => void;
  styleType?: 'default' | 'ordering';
};

export type MountData = {
  height: number;
};

function DroppableArea({
  droppableId,
  droppableText,
  draggableId,
  draggableText,
  type,
  correct,
  incorrect,
  disabled,
  minHeight,
  width,
  onMount,
  styleType,
}: DroppableAreaProps) {
  const [mountData, setMountData] = useState<MountData>();
  const completed: boolean = !!droppableText && !!draggableText;

  useEffect(() => {
    if (mountData && onMount) onMount(mountData);
  }, [mountData]); // eslint-disable-line

  function handleRef(el: HTMLDivElement) {
    if (mountData || !el?.clientHeight) return;
    setMountData({ height: el?.clientHeight });
  }

  return (
    <Droppable droppableId={droppableId} direction="horizontal">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          className={[
            styles.dropzone,
            styles[`dropzone_${type}`],
            styleType === 'ordering'
              ? styles[`dropzone_${type}_${styleType}`]
              : '',
            snapshot.isDraggingOver ? styles.dropzoneIsDragging : '',
          ].join(' ')}
          style={{
            minHeight,
            width: width
              ? type === 'source'
                ? `${width}px`
                : `${width * 2}px`
              : undefined,
          }}
        >
          {draggableId && draggableText && (
            <DraggableItem
              draggableId={draggableId}
              draggableText={draggableText}
              index={0}
              isDraggingOver={
                snapshot.isDraggingOver &&
                // to avoid the effect if is dragging over on itself
                snapshot.draggingOverWith !== snapshot.draggingFromThisWith
              }
              className={styles.text}
              correct={completed && !!correct}
              incorrect={completed && !!incorrect}
              disabled={disabled}
              onMount={onMount}
              minHeight={minHeight}
            />
          )}
          {droppableText && (
            <div
              className={[
                styles.droppableTextWrapper,
                styles.droppableTextWrapperWithArrow,
                draggableId && draggableText
                  ? styles.droppableTextWrapperNoLeftBorder
                  : '',
                completed && correct ? styles.droppableTextWrapperCorrect : '',
                completed && incorrect
                  ? styles.droppableTextWrapperIncorrect
                  : '',
              ].join(' ')}
              ref={handleRef}
              style={{ minHeight }}
            >
              <div className={styles.text}>{droppableText}</div>
            </div>
          )}
          <div style={{ display: 'none' }}>{provided.placeholder}</div>
        </div>
      )}
    </Droppable>
  );
}

export default DroppableArea;
